/**
 * Media page (M9.5) — AI Media Engine.
 *
 * Tabs: Reel Builder (default) · Recipes · Assets · Providers · Router · Memory.
 * Reel Builder is the wow surface — one intent → five finished channel
 * outputs (real SVG posters, MP4 videos, storyboard + shot-list PDFs,
 * silent WAV, SRT captions). Every value below comes from the M9.5 API;
 * feature code never touches a provider SDK.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight, Eye, EyeOff, FileText, Film, Gauge, ImageIcon,
  Layers, ListChecks, Loader2, Mic, PlayCircle, Repeat, Route, ShieldCheck,
  Sparkles, Sprout, Wand2, DollarSign, type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

import { useCurrentWorkspace } from "@/entities/workspace";
import { useFounderMode } from "@/entities/auth/useFounderMode";
import { useSurfacePolicy } from "@/entities/auth/useSurfacePolicy";
import { PremiumEmpty } from "@/shared/ui/premium-empty";
import { SafeMedia } from "@/shared/media/SafeMedia";
import {
  createRecipe, DEFAULT_REEL_CHANNELS, generateReel, getDiagnosticsState,
  listAssets, listMemory, listProviders, listRecipes, listRouterDecisions,
  READINESS_TONES, renderRecipe, rerenderRecipe, seedSampleAssets,
  setDiagnosticsEnabled, STATUS_TONES,
  type MediaAsset, type MediaKind, type MediaRecipe, type MemoryEntry,
  type ProvidersMap, type ReelResult, type RouterPolicy,
} from "@/entities/media";
import { useEntitlements, useUpgradePrompt } from "@/features/entitlements/useEntitlements";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { config } from "@/shared/config";
import { HorizontalScrollCue } from "@/shared/ui/horizontal-scroll-cue";

const CHANNEL_LABEL: Record<string, string> = {
  instagram_reel: "Instagram Reel",
  facebook_reel: "Facebook Reel",
  tiktok: "TikTok",
  youtube_short: "YouTube Short",
  pinterest_idea_pin: "Pinterest Idea Pin",
};

const cur = (v: number) => `$${v.toFixed(v < 1 ? 4 : 2)}`;
const rel = (u: string | null | undefined) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${config.backendUrl}${u.startsWith("/") ? u : `/${u}`}`;
};

const KIND_ICON: Record<MediaKind, LucideIcon> = {
  image: ImageIcon, video: Film, voice: Mic, music: Mic,
  reel: Film, thumbnail_set: Layers, caption: FileText,
};

export default function MediaPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const founderMode = useFounderMode();
  if (!workspace) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <PremiumEmpty
          testId="media-empty"
          icon={<Film className="h-5 w-5" />}
          eyebrow="Media Engine"
          title="One intent in. Finished, brand-locked assets out."
          body="The Media Engine turns a one-line intent into finished channel-native posters, reels, storyboards, and captions — every provider behind one interface."
          bullets={[
            "Ships an on-brand reel in under 60 seconds",
            "Auto-cropped for IG, TikTok, YouTube Short",
            "Every render logged with cost, latency, brand-lock",
          ]}
          primary={{ label: "Open a workspace", to: "/", testId: "media-empty-open" }}
          secondary={{ label: "Explore the demo", to: "/demo" }}
        />
      </div>
    );
  }
  return (
    <div data-testid="media-page" className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <Sparkles className="h-3 w-3" /> M9.5 · Finished assets, not prompts
          </div>
          <h1 data-testid="media-title" className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            Media Engine
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            One intent in — finished, brand-locked, cost-tracked outputs out. Every provider sits behind an
            interface: swap Sora for Veo without touching a feature file.
          </p>
        </div>
      </header>

      <Tabs defaultValue="reel-builder" className="space-y-4">
        <HorizontalScrollCue testId="media-tabs-scroll">
        <TabsList data-testid="media-tabs" className="flex h-auto w-max min-w-full justify-start gap-1 whitespace-nowrap">
          <TabsTrigger data-testid="tab-reel-builder" value="reel-builder">Reel Builder</TabsTrigger>
          <TabsTrigger data-testid="tab-recipes" value="recipes">Recipes</TabsTrigger>
          <TabsTrigger data-testid="tab-assets" value="assets">Assets</TabsTrigger>
        </TabsList>
        </HorizontalScrollCue>

        {founderMode ? (
          <details data-testid="media-founder-tools" className="rounded-lg border border-border/60 bg-card/40 p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Founder tools
            </summary>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <SeedAssetsButton ws={workspace.id} />
              <DiagnosticsToggle ws={workspace.id} />
            </div>
            <HorizontalScrollCue testId="media-founder-tabs-scroll">
              <TabsList data-testid="media-founder-tabs" className="mt-3 flex h-auto w-max min-w-full justify-start gap-1 whitespace-nowrap">
                <TabsTrigger data-testid="tab-providers" value="providers">Providers</TabsTrigger>
                <TabsTrigger data-testid="tab-router" value="router">Router</TabsTrigger>
                <TabsTrigger data-testid="tab-memory" value="memory">Memory</TabsTrigger>
              </TabsList>
            </HorizontalScrollCue>
          </details>
        ) : null}

        <TabsContent value="reel-builder" className="space-y-4"><ReelBuilderPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="recipes" className="space-y-4"><RecipesPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="assets" className="space-y-4"><AssetsPanel ws={workspace.id} /></TabsContent>
        {founderMode ? (
          <>
            <TabsContent value="providers" className="space-y-4"><ProvidersPanel /></TabsContent>
            <TabsContent value="router" className="space-y-4"><RouterPanel ws={workspace.id} /></TabsContent>
            <TabsContent value="memory" className="space-y-4"><MemoryPanel ws={workspace.id} /></TabsContent>
          </>
        ) : null}
      </Tabs>
    </div>
  );
}

/* ── Diagnostics toggle (founder-only) ────────────────────────── */

function DiagnosticsToggle({ ws }: { ws: string }): JSX.Element | null {
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: ["media", "diagnostics-state", ws],
    queryFn: () => getDiagnosticsState(ws),
    staleTime: 30_000,
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => setDiagnosticsEnabled(ws, enabled),
    onSuccess: (data) => {
      qc.setQueryData(["media", "diagnostics-state", ws], data);
      qc.invalidateQueries({ queryKey: ["media", "assets", ws] });
    },
  });

  if (!state.data || !state.data.can_toggle) return null;
  const enabled = state.data.diagnostics_enabled;
  return (
    <button
      type="button"
      data-testid="media-diagnostics-toggle"
      onClick={() => toggle.mutate(!enabled)}
      disabled={toggle.isPending}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition ${
        enabled
          ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
          : "border-border/60 bg-background/60 text-muted-foreground hover:text-foreground"
      }`}
    >
      {enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      Diagnostics {enabled ? "on" : "off"}
    </button>
  );
}

/* ── Preview/demo asset seeder (founder-only) ─────────────────── */

function SeedAssetsButton({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const seed = useMutation({
    mutationFn: () => seedSampleAssets(ws),
    onSuccess: (data) => {
      toast.success(`Seeded ${data.seeded} sample assets. ${data.total_assets} in workspace.`);
      qc.invalidateQueries({ queryKey: ["media", "assets", ws] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not seed assets");
    },
  });
  return (
    <button
      type="button"
      data-testid="media-seed-assets"
      onClick={() => seed.mutate()}
      disabled={seed.isPending}
      className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-emerald-300 transition hover:bg-emerald-500/20 disabled:opacity-60"
    >
      {seed.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sprout className="h-3.5 w-3.5" />}
      {seed.isPending ? "Seeding…" : "Seed sample assets"}
    </button>
  );
}

/* ── Reel Builder ─────────────────────────────────────────────── */

function ReelBuilderPanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const { canViewInternalDiagnostics } = useSurfacePolicy();
  const [intent, setIntent] = useState("Promote Dallisgrass removal weekend deal");
  const [channels, setChannels] = useState<string[]>(DEFAULT_REEL_CHANNELS);
  const [result, setResult] = useState<ReelResult | null>(null);

  // Estimate via a cheap create-then-drop path is expensive; use a static estimate
  // per selected channel (matches server-side cheapest-brand-locked pool).
  const perChannelEstimate = 0.94; // video + poster + voice + captions defaults
  const estimate = perChannelEstimate * channels.length;

  const build = useMutation({
    mutationFn: () => generateReel(ws, { intent, channels }),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Rendered ${r.child_assets.length} channel packages`);
      qc.invalidateQueries({ queryKey: ["media"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (c: string) => setChannels((prev) => prev.includes(c)
    ? prev.filter((x) => x !== c) : [...prev, c]);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card data-testid="reel-builder-card" className="border-border/60 bg-card/70">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-indigo-500" />
            <CardTitle className="font-display text-2xl">One intent — five finished outputs</CardTitle>
          </div>
          <CardDescription>
            Each channel gets its own aspect ratio, pacing, dialogue, voiceover, captions, storyboard PDF,
            shot-list PDF, and MP4 video — brand-locked from your Brand Brain.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reel-intent" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Intent
            </Label>
            <Input
              id="reel-intent"
              data-testid="reel-intent-input"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="Describe the campaign goal in one sentence."
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Channels</Label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_REEL_CHANNELS.map((c) => (
                <button
                  key={c}
                  data-testid={`reel-channel-${c}`}
                  type="button"
                  onClick={() => toggle(c)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    channels.includes(c)
                      ? "border-indigo-400/60 bg-indigo-500/10 text-foreground"
                      : "border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {CHANNEL_LABEL[c] ?? c}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-md border border-border/50 bg-background/60 p-3 text-xs">
            {/* Patch 37.4.12 — dollar estimate is internal cost data.
                Public demo + customer surfaces see only outcome
                affordances (Brand lock, Rerenderable). */}
            {canViewInternalDiagnostics ? (
              <div className="flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                <span className="font-mono uppercase tracking-widest text-muted-foreground">Estimated cost</span>
                <span className="font-semibold tabular-nums">{cur(estimate)}</span>
              </div>
            ) : null}
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-indigo-500" />
              <span className="font-mono uppercase tracking-widest text-muted-foreground">Brand lock</span>
              <span className="font-semibold">from Brand Brain</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Repeat className="h-3.5 w-3.5 text-sky-500" />
              <span className="font-mono uppercase tracking-widest text-muted-foreground">Rerenderable</span>
              <span className="font-semibold">any provider</span>
            </div>
          </div>

          <Button
            data-testid="reel-build-button"
            onClick={() => build.mutate()}
            disabled={build.isPending || !intent.trim() || channels.length === 0}
            className="w-full sm:w-auto"
          >
            {build.isPending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering…</>
            ) : (
              <><PlayCircle className="mr-2 h-4 w-4" /> Build reel</>
            )}
          </Button>

          {result ? <ReelResultBlock result={result} /> : null}
        </CardContent>
      </Card>

      <aside className="space-y-3">
        <Card className="border-border/60 bg-card/70">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 font-display text-base">
              <Gauge className="h-4 w-4 text-indigo-500" /> How it composes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs text-muted-foreground">
            <p>1. Brand snapshot frozen from your Brand Brain — palette, voice, CTA.</p>
            <p>2. AI-suggested scenes populate a real Scene JSON manifest.</p>
            <p>3. Router picks the cheapest brand-lock-passing provider per capability.</p>
            <p>4. Rendering emits real SVG posters, PDF storyboards, PDF shot lists, silent WAV voice, and SRT captions.</p>
            <p>5. Quality score is composited from six sub-scores; assets below threshold auto-reject.</p>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}

function ReelResultBlock({ result }: { result: ReelResult }): JSX.Element {
  const { canViewInternalDiagnostics } = useSurfacePolicy();
  return (
    <div data-testid="reel-result" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-500/40 bg-emerald-50/50 p-3 text-sm dark:bg-emerald-950/20">
        <div className="flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold">{result.child_assets.length} channels rendered</span>
        </div>
        {/* Patch 37.4.12 — total spend is internal cost information.
            Customer surfaces + demos see the finished count only. */}
        {canViewInternalDiagnostics ? (
          <span className="font-mono text-xs">total ~ {cur(result.cost_estimate_total_usd)}</span>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {result.child_assets.map((c) => (
          <div
            key={c.recipe_id}
            data-testid={`reel-channel-result-${c.channel}`}
            className="rounded-md border border-border/50 bg-background/60 p-3 text-xs"
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold">{CHANNEL_LABEL[c.channel] ?? c.channel}</span>
              <Badge className={`${STATUS_TONES[c.status] ?? ""} font-mono text-[10px] uppercase`}>{c.status}</Badge>
            </div>
            {/* Patch 37.4.12 — quality score + recipe hash are internal
                diagnostics. Never surfaced on customer or demo screens. */}
            {canViewInternalDiagnostics ? (
              <p className="mt-1 text-muted-foreground">quality {c.quality_score}/100 · recipe {c.recipe_id.slice(0, 8)}</p>
            ) : null}
            {c.primary_url ? (
              <a
                href={rel(c.primary_url)}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-indigo-500 hover:underline"
                data-testid={`reel-channel-download-${c.channel}`}
              >
                Download MP4 video <ArrowRight className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Recipes ─────────────────────────────────────────────────── */

function RecipesPanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const [kind, setKind] = useState<MediaKind>("image");
  const [intent, setIntent] = useState("");
  const q = useQuery({ queryKey: ["media", "recipes", ws], queryFn: () => listRecipes(ws) });

  // Patch 26 — Free-plan preview lock. The Video Generator remains
  // visible for discovery, but we never queue provider jobs, reserve
  // usage, or spend a single dollar on providers.
  const ent = useEntitlements();
  const upgrade = useUpgradePrompt();
  const kindIsPremiumBlocked =
    (kind === "video" && !ent.canGenerateVideo()) ||
    (kind === "voice" && !ent.canGenerateVoice()) ||
    (kind === "image" && !ent.canGenerateImage()) ||
    (kind === "thumbnail_set" && !ent.canGenerateImage());
  const lockCopy: Record<string, { title: string; reason: string }> = {
    video: {
      title: "Premium video is a paid capability",
      reason: "AI Video Generation is available with Starter or higher. Upgrade to unlock premium video creation.",
    },
    voice: {
      title: "Voice generation is a paid capability",
      reason: "Voice minutes are available with Starter or higher. Upgrade to add a voice track to your reels.",
    },
    image: {
      title: "You've reached your monthly premium image allowance",
      reason: "Upgrade or purchase an add-on pack to keep creating premium images.",
    },
    thumbnail_set: {
      title: "You've reached your monthly premium image allowance",
      reason: "Thumbnail sets consume premium images. Upgrade or purchase an add-on pack to continue.",
    },
  };

  const create = useMutation({
    mutationFn: () => createRecipe(ws, { kind, intent }),
    onSuccess: () => { toast.success("Recipe created"); qc.invalidateQueries({ queryKey: ["media"] }); setIntent(""); },
  });

  const render = useMutation({
    mutationFn: ({ id, policy }: { id: string; policy: RouterPolicy }) => renderRecipe(ws, id, policy),
    onSuccess: (a) => {
      toast.success(a.status === "ready" ? "Rendered" : a.status === "rejected"
        ? `Rejected — ${a.reject_reason ?? ""}` : "Complete");
      qc.invalidateQueries({ queryKey: ["media"] });
    },
    onError: (e: unknown) => {
      // Patch 26 — HTTP 402 entitlement denials open the UpgradeModal.
      const err = e as { status?: number; message?: string; details?: Record<string, unknown> };
      if (err?.status === 402) {
        upgrade.open({
          title: "Upgrade to keep creating",
          reason: err.message ?? "You've reached your monthly allowance.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
          addon_kind: err.details?.addon_kind as string | undefined,
        });
        return;
      }
      toast.error(err?.message ?? "Something went wrong");
    },
  });

  const rerender = useMutation({
    mutationFn: ({ id, policy }: { id: string; policy: RouterPolicy }) => rerenderRecipe(ws, id, policy),
    onSuccess: () => { toast.success("Rerendered"); qc.invalidateQueries({ queryKey: ["media"] }); },
  });

  return (
    <div className="space-y-4">
      <Card data-testid="recipes-create-card" className="border-border/60 bg-card/70">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-lg">New recipe</CardTitle>
          <CardDescription>Every rendered asset starts as a portable, replayable recipe.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-[140px_minmax(0,1fr)_auto]">
          <div className="space-y-1">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Kind</Label>
            <select
              data-testid="recipe-kind-select"
              className="w-full rounded-md border border-border/60 bg-background/60 px-2 py-2 text-sm"
              value={kind}
              onChange={(e) => setKind(e.target.value as MediaKind)}
            >
              <option value="image">image</option>
              <option value="video">video</option>
              <option value="voice">voice</option>
              <option value="thumbnail_set">thumbnail_set</option>
              <option value="caption">caption</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Intent</Label>
            <Input
              data-testid="recipe-intent-input"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="What should this asset accomplish?"
            />
          </div>
          <div className="flex items-end">
            <Button
              data-testid="recipe-create-button"
              onClick={() => {
                if (kindIsPremiumBlocked) {
                  const copy = lockCopy[kind];
                  upgrade.open({
                    title: copy.title,
                    reason: copy.reason,
                    upgrade_target: kind === "video" || kind === "voice" ? "starter_v2" : undefined,
                    addon_kind: kind === "image" || kind === "thumbnail_set" ? "images" : undefined,
                  });
                  return;
                }
                create.mutate();
              }}
              disabled={create.isPending || !intent.trim()}
            >
              {kindIsPremiumBlocked ? "Upgrade to unlock" : "Create"}
            </Button>
          </div>
        </CardContent>
        {kindIsPremiumBlocked && (
          <div
            data-testid={`recipe-preview-lock-${kind}`}
            className="mx-6 mb-4 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs text-amber-200/90"
          >
            <div className="font-mono uppercase tracking-widest text-[10px] text-amber-300/80">Preview mode</div>
            <div className="mt-1 text-sm text-amber-100">{lockCopy[kind].reason}</div>
            <div className="mt-1 text-[11px] text-amber-200/70">
              No provider jobs are queued and no credits are consumed on the Free plan.
            </div>
          </div>
        )}
      </Card>

      <Card data-testid="recipes-list-card" className="border-border/60 bg-card/70">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-lg">Recipes</CardTitle>
          <CardDescription>Every recipe is provider-agnostic. Rerender any time to swap providers.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {q.isLoading ? <Skeleton className="h-32 w-full" /> :
            q.data && q.data.length > 0 ? q.data.map((r) => (
              <RecipeRow
                key={r._id ?? r.id}
                recipe={r}
                onRender={(policy) => render.mutate({ id: (r._id ?? r.id) as string, policy })}
                onRerender={(policy) => rerender.mutate({ id: (r._id ?? r.id) as string, policy })}
                busy={render.isPending || rerender.isPending}
              />
            )) : <EmptyLine label="No recipes yet — build one via the Reel Builder or the form above." />}
        </CardContent>
      </Card>
    </div>
  );
}

function RecipeRow({
  recipe, onRender, onRerender, busy,
}: {
  recipe: MediaRecipe;
  onRender: (policy: RouterPolicy) => void;
  onRerender: (policy: RouterPolicy) => void;
  busy: boolean;
}): JSX.Element {
  const { canViewInternalDiagnostics } = useSurfacePolicy();
  const Icon = KIND_ICON[recipe.kind] ?? FileText;
  const id = (recipe._id ?? recipe.id) as string;
  return (
    <div data-testid={`recipe-row-${id}`} className="rounded-md border border-border/50 bg-background/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4 text-indigo-500" />
        <span className="text-sm font-semibold">{recipe.intent}</span>
        <Badge className={`${STATUS_TONES[recipe.status] ?? ""} font-mono text-[10px] uppercase`}>{recipe.status}</Badge>
        <Badge variant="outline" className="font-mono text-[10px]">{recipe.kind}</Badge>
        {recipe.channel ? <Badge variant="outline" className="font-mono text-[10px]">{recipe.channel}</Badge> : null}
        <Badge variant="outline" className="font-mono text-[10px]">{recipe.aspect_ratio}</Badge>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
        <Stat label="Readiness" value={`${recipe.readiness?.overall_pct ?? 0}%`}
              tone={READINESS_TONES(recipe.readiness?.overall_pct ?? 0)} />
        {/* Patch 37.4.12 — Cost est., Quality score, and Router pick
            are internal diagnostics. Customer + demo surfaces see
            Readiness + Scenes only (the outcome-facing stats). */}
        {canViewInternalDiagnostics ? (
          <>
            <Stat label="Cost est." value={cur(recipe.cost_estimate?.total_usd ?? 0)} />
            <Stat label="Quality" value={`${recipe.telemetry?.quality_score ?? "—"}${recipe.telemetry ? "/100" : ""}`}
                  tone={recipe.telemetry ? READINESS_TONES(recipe.telemetry.quality_score) : "text-muted-foreground"} />
          </>
        ) : null}
        <Stat label="Scenes" value={String(recipe.scenes.length)} />
      </div>
      {canViewInternalDiagnostics && recipe.router_decision ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          <Route className="mr-1 inline h-3 w-3" />
          picked <span className="font-mono text-foreground">{recipe.router_decision.provider_key}</span>
          {" — "}{recipe.router_decision.reason}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <Button size="sm" variant="outline" data-testid={`recipe-render-${id}`}
                onClick={() => onRender("cheapest_brand_locked")} disabled={busy}>
          <PlayCircle className="mr-1 h-3.5 w-3.5" /> Render (cheapest)
        </Button>
        <Button size="sm" variant="ghost" data-testid={`recipe-rerender-${id}`}
                onClick={() => onRerender("highest_quality")} disabled={busy}>
          <Repeat className="mr-1 h-3.5 w-3.5" /> Rerender · highest quality
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border/50 bg-background/60 p-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-display text-sm font-semibold tabular-nums ${tone ?? ""}`}>{value}</div>
    </div>
  );
}

/* ── Assets ──────────────────────────────────────────────────── */

function AssetsPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["media", "assets", ws], queryFn: () => listAssets(ws) });
  return (
    <Card data-testid="assets-card" className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg">Rendered assets</CardTitle>
        <CardDescription>Real bytes: SVG posters, MP4 videos, PDF storyboards + shot lists, silent WAVs, SRT captions.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {q.isLoading ? <Skeleton className="h-40 w-full" /> :
          q.data && q.data.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {q.data.map((a) => <AssetCard key={a._id ?? a.id} asset={a} />)}
            </div>
          ) : <EmptyLine label="No rendered assets yet." />}
      </CardContent>
    </Card>
  );
}

function AssetCard({ asset }: { asset: MediaAsset }): JSX.Element {
  const Icon = KIND_ICON[asset.kind] ?? FileText;
  const id = (asset._id ?? asset.id) as string;
  const status = String(asset.status ?? "");
  const isSimulation = status === "simulation_complete" || (asset as any).execution_mode === "simulation";
  const isFailedValidation = status === "failed_validation";
  const isReady = status === "ready" && (asset as any).production_ready === true;
  const thumbnailUrl = (asset as any).thumbnail_ref?.url ?? null;
  const posterUrl = thumbnailUrl ?? asset.poster_asset_ref?.url ?? asset.primary_asset_ref?.url ?? null;
  const validated = (asset as any).validated_media ?? {};
  const failureReason = (asset as any).failure_reason ?? asset.reject_reason;
  const showDiagnostics = Boolean((asset as any)._diagnostics_visible);
  const showCost = showDiagnostics && typeof asset.telemetry?.cost_usd === "number";
  const statusTone: Record<string, string> = {
    ready: "bg-emerald-500/15 text-emerald-500",
    simulation_complete: "bg-amber-500/15 text-amber-500",
    failed_validation: "bg-rose-500/15 text-rose-500",
    failed_render: "bg-rose-500/15 text-rose-500",
    rendering: "bg-indigo-500/15 text-indigo-500",
    rejected: "bg-zinc-500/15 text-zinc-500",
    archived: "bg-zinc-500/15 text-zinc-500",
  };
  const statusLabel: Record<string, string> = {
    ready: "Ready",
    simulation_complete: "Simulation Complete",
    failed_validation: "Failed Validation",
    failed_render: "Render Failed",
    rendering: "Rendering",
    rejected: "Rejected",
    archived: "Archived",
  };
  return (
    <div data-testid={`asset-card-${id}`} className="rounded-md border border-border/50 bg-background/60 p-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-indigo-500" />
        <span className="text-sm font-semibold">{asset.kind}</span>
        <Badge
          data-testid={`asset-status-${id}`}
          className={`${statusTone[status] ?? STATUS_TONES[asset.status] ?? ""} font-mono text-[10px] uppercase`}
        >
          {statusLabel[status] ?? status}
        </Badge>
      </div>
      {/* Simulation banner — never render a fake video player. */}
      {isSimulation ? (
        <div
          data-testid={`asset-simulation-banner-${id}`}
          className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-600"
        >
          Preview workflow completed. No playable media was generated.
        </div>
      ) : isFailedValidation ? (
        <div
          data-testid={`asset-validation-failed-${id}`}
          className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-[11px] text-rose-600"
        >
          This asset failed physical validation and is not available for download.
          {failureReason ? <div className="mt-1 text-[10px] opacity-80">{failureReason}</div> : null}
        </div>
      ) : isReady && (asset.kind === "video" || asset.kind === "reel") && asset.primary_asset_ref?.url ? (
        <SafeMedia
          kind="video"
          testId={`asset-video-${id}`}
          src={rel(asset.primary_asset_ref.url)}
          poster={thumbnailUrl ? rel(thumbnailUrl) : null}
          controls
          preload="metadata"
          className="mt-2 h-32 w-full rounded border border-border/40 bg-background/50 object-cover"
        />
      ) : posterUrl ? (
        <SafeMedia
          kind="image"
          testId={`asset-preview-${id}`}
          src={rel(posterUrl)}
          alt="preview"
          className="mt-2 h-32 w-full rounded border border-border/40 bg-background/50 object-cover"
        />
      ) : (
        <SafeMedia
          kind="image"
          testId={`asset-preview-${id}`}
          src={null}
          emptyLabel="No preview yet. This asset has no media attached."
          className="mt-2"
        />
      )}
      {/* Production metadata (customer-safe) — validated media stats only. */}
      {isReady && validated.duration_seconds ? (
        <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] text-muted-foreground">
          <div>{Math.round(validated.duration_seconds)}s</div>
          <div>{validated.width}×{validated.height}</div>
          <div className="uppercase">{validated.video_codec ?? validated.image_format ?? ""}</div>
        </div>
      ) : null}
      {/* Diagnostics — admin/owner only, cost + provider visible here. */}
      {showDiagnostics ? (
        <div
          data-testid={`asset-diagnostics-${id}`}
          className="mt-2 rounded-md border border-dashed border-indigo-400/50 bg-indigo-500/5 p-2 text-[10px] text-indigo-500"
        >
          <div className="mb-1 flex items-center gap-1 font-semibold uppercase tracking-widest">
            <ShieldCheck className="h-3 w-3" /> Diagnostics Enabled
          </div>
          {(asset as any).provider_key ? <div>Provider: <span className="font-mono">{(asset as any).provider_key}</span></div> : null}
          {(asset as any).provider_job_id ? <div>Job ID: <span className="font-mono">{(asset as any).provider_job_id}</span></div> : null}
          {showCost ? <div>Cost: {cur(asset.telemetry.cost_usd)}</div> : null}
          {asset.telemetry?.quality_score ? <div>Quality: {asset.telemetry.quality_score}/100</div> : null}
        </div>
      ) : null}
      {/* Downloads — only when READY and validated. Simulation gets NO download. */}
      {isReady ? (
        <div className="mt-2 flex flex-wrap gap-1 text-[11px]">
          {asset.primary_asset_ref ? (
            <a
              href={rel(asset.primary_asset_ref.url)} target="_blank" rel="noreferrer"
              data-testid={`asset-download-${id}`}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 hover:bg-secondary"
            >
              Download <ArrowRight className="h-3 w-3" />
            </a>
          ) : null}
          {asset.storyboard_pdf_ref ? (
            <a href={rel(asset.storyboard_pdf_ref.url)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 hover:bg-secondary">
              Storyboard <ArrowRight className="h-3 w-3" />
            </a>
          ) : null}
          {asset.shot_list_pdf_ref ? (
            <a href={rel(asset.shot_list_pdf_ref.url)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 hover:bg-secondary">
              Shot list <ArrowRight className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      ) : isSimulation ? (
        <div className="mt-2 flex flex-wrap gap-1 text-[11px]">
          {asset.storyboard_pdf_ref ? (
            <a href={rel(asset.storyboard_pdf_ref.url)} target="_blank" rel="noreferrer"
               data-testid={`asset-storyboard-${id}`}
               className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 hover:bg-secondary">
              View production brief <ArrowRight className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      ) : null}
      {failureReason && !isFailedValidation ? (
        <p className="mt-2 text-[11px] text-rose-500" data-testid={`asset-reject-${id}`}>{failureReason}</p>
      ) : null}
    </div>
  );
}

/* ── Providers ───────────────────────────────────────────────── */

function ProvidersPanel(): JSX.Element {
  const q = useQuery({ queryKey: ["media", "providers"], queryFn: listProviders });
  const providers: ProvidersMap = q.data ?? {};
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {Object.entries(providers).map(([kind, arr]) => (
        <Card key={kind} data-testid={`provider-card-${kind}`} className="border-border/60 bg-card/70">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-base capitalize">{kind}</CardTitle>
            <CardDescription>Every provider registers under a single interface.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 text-xs">
            {arr.length === 0 ? <span className="text-muted-foreground">Interface only in M9.5.</span> :
              arr.map((p) => (
                <div key={p.key} className="flex items-center justify-between rounded-md border border-border/50 bg-background/60 px-2 py-1">
                  <span className="font-mono">{p.key}</span>
                  <span className="text-muted-foreground">{cur(p.cost_per_call_usd)} · q {p.quality_score}</span>
                </div>
              ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ── Router decisions ─────────────────────────────────────────── */

function RouterPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["media", "router", ws], queryFn: () => listRouterDecisions(ws) });
  return (
    <Card data-testid="router-card" className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg">Router audit</CardTitle>
        <CardDescription>Every provider selection is deterministic and logged.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {q.isLoading ? <Skeleton className="h-24 w-full" /> :
          q.data && q.data.length > 0 ? q.data.map((d) => (
            <div key={d._id} data-testid={`router-row-${d._id}`}
                 className="rounded-md border border-border/50 bg-background/60 p-2 text-xs">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-[10px]">{d.kind}</Badge>
                {d.channel ? <Badge variant="outline" className="font-mono text-[10px]">{d.channel}</Badge> : null}
                <span className="font-medium">{d.intent}</span>
              </div>
              {d.router_decision ? (
                <p className="mt-1 text-muted-foreground">
                  → <span className="font-mono text-foreground">{d.router_decision.provider_key}</span>
                  {" · policy "}<span className="font-mono">{d.router_decision.policy}</span>
                  {" — "}{d.router_decision.reason}
                </p>
              ) : null}
            </div>
          )) : <EmptyLine label="No routing decisions yet." />}
      </CardContent>
    </Card>
  );
}

/* ── Memory ──────────────────────────────────────────────────── */

function MemoryPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["media", "memory", ws], queryFn: () => listMemory(ws) });
  return (
    <Card data-testid="memory-card" className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg">Media memory</CardTitle>
        <CardDescription>Winning patterns per workspace. Bootstrapped from Brand Brain, evolved from telemetry.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {q.isLoading ? <Skeleton className="h-24 w-full" /> :
          q.data && q.data.length > 0 ? q.data.map((m: MemoryEntry) => (
            <div key={m._id} data-testid={`memory-row-${m._id}`}
                 className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/50 bg-background/60 p-2 text-xs">
              <div className="flex items-center gap-2">
                <ListChecks className="h-3.5 w-3.5 text-indigo-500" />
                <span className="font-mono">{m.kind}</span>
                <span className="text-muted-foreground">{JSON.stringify(m.value)}</span>
              </div>
              <div className="flex items-center gap-2 font-mono">
                <span>score {m.score.toFixed(0)}</span>
                <Badge variant="outline" className="font-mono text-[10px]">{m.source}</Badge>
              </div>
            </div>
          )) : <EmptyLine label="Media memory bootstraps on first request." />}
      </CardContent>
    </Card>
  );
}

/* ── shared ──────────────────────────────────────────────────── */

function EmptyLine({ label }: { label: string }): JSX.Element {
  return <div className="rounded-md border border-dashed border-border/50 bg-background/40 p-6 text-center text-xs text-muted-foreground">{label}</div>;
}
